// reviewLabels.ts — resolve a Review Mode comment's target (a step or a module)
// into a human "ACT › Chapter › Step" label, so the queue and the export read in
// the same terms as the content manifest. Labels are resolved at display/export
// time from the live programme (not stored on the comment), so they stay correct
// if content is re-titled.
import type { Programme } from '@/types';
import type { ReviewComment } from '@/services/progress';
import { findStep, findModule, moduleOfStep, moduleName, courseOfModule } from '@/services/contentProvider';

export interface ReviewLabelParts {
  act: string;
  chapter: string;
  /** Present for step-level comments only. */
  step?: string;
  /** Ordering keys, so an export reads in programme order. */
  actOrder: number;
  chapterOrder: number;
  stepOrder: number;
}

function flatTitle(t?: { pre?: string; em?: string; post?: string }): string {
  if (!t) return '';
  return [t.pre, t.em, t.post].filter(Boolean).join('').trim();
}

export function labelPartsFor(programme: Programme, comment: ReviewComment): ReviewLabelParts {
  const isChapter = comment.kind === 'chapter';
  const module = isChapter ? findModule(programme, comment.targetId) : moduleOfStep(programme, comment.targetId);
  const course = module ? courseOfModule(programme, module.id) : undefined;
  const step = isChapter ? undefined : findStep(programme, comment.targetId);
  return {
    act: course?.fullTitle || course?.title || '—',
    chapter: module ? moduleName(module) : (isChapter ? comment.targetId : '—'),
    step: isChapter ? undefined : step ? flatTitle(step.title) || comment.targetId : comment.targetId,
    actOrder: course?.order ?? 999,
    chapterOrder: module?.order ?? 999,
    stepOrder: step?.order ?? (isChapter ? -1 : 999),
  };
}

/** One-line "ACT › Chapter › Step" rendering of the parts. */
export function labelLine(parts: ReviewLabelParts): string {
  return [parts.act, parts.chapter, parts.step].filter(Boolean).join('  ›  ');
}
